import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMyMonthAssignments, patchShiftScheduleCell } from '../../lib/shiftApi';
import { useShift } from '../../context/ShiftContext';
import { useShiftI18n } from '../../i18n/ShiftI18nContext';
import ScheduleSheet from './ScheduleSheet';
import './shift.css';

function currentMonthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function ShiftMyMonthSchedule() {
  const nav = useNavigate();
  const { isOwner, user } = useShift() || {};
  const { t } = useShiftI18n();
  const [offset, setOffset] = useState(0);
  const month = useMemo(() => currentMonthKey(offset), [offset]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('mine');
  const [mobileList, setMobileList] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetchMyMonthAssignments(month, view);
      if (!r?.ok) { setErr(r?.error || '載入失敗'); setData(null); return; }
      setData(r);
      if (r.canSeeAll && view === 'all' && !r.published && !r.canEdit) {
        setView('mine');
      }
    } catch {
      setErr('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [month, view]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    setMobileList(mq.matches);
  }, []);

  const byDay = useMemo(() => {
    const m = {};
    for (const a of data?.assignments || []) m[a.day] = a;
    return m;
  }, [data]);

  const onPatch = async (payload) => {
    if (!data?.canEdit) return;
    setSaveStatus('儲存中');
    try {
      const r = await patchShiftScheduleCell({ month, ...payload });
      if (r?.ok) {
        setSaveStatus('已存');
        load();
      } else setErr(r?.error || '更新失敗');
    } finally {
      setTimeout(() => setSaveStatus(''), 1200);
    }
  };

  const phone = data?.people?.find((p) => p.person === data.person)?.staffPhone
    || user?.phone || '';
  const showSheet = !mobileList || view === 'all';

  return (
    <>
        <div className="shift-topbar shift-topbar-compact">
          <div className="shift-tb-row">
            <button type="button" className="shift-back" onClick={() => nav('/shift/home')}>←</button>
            <div className="shift-tb-info">
              <div className="shift-tb-title">{t('month.title')}</div>
              <div className="shift-tb-sub">{data?.person || ''} · {data?.published ? t('month.published') : t('month.unpublished')}</div>
            </div>
          </div>
          <div className="shift-tb-row" style={{ justifyContent: 'space-between' }}>
            <button type="button" className="avail-month-btn" disabled={offset <= -1} onClick={() => setOffset((v) => v - 1)}>{t('month.prev')}</button>
            <strong>{month}</strong>
            <button type="button" className="avail-month-btn" disabled={offset >= 1} onClick={() => setOffset((v) => v + 1)}>{t('month.next')}</button>
          </div>
          <div className="shift-tb-row" style={{ gap: 8 }}>
            <button type="button" className={`shift-mb-btn ${view === 'mine' ? 'on' : ''}`} onClick={() => setView('mine')}>{t('month.mine')}</button>
            <button
              type="button"
              className={`shift-mb-btn ${view === 'all' ? 'on' : ''}`}
              disabled={!data?.canSeeAll && view !== 'all'}
              onClick={() => setView('all')}
            >{t('month.all')}</button>
            <button type="button" className="shift-mb-btn" onClick={() => setMobileList((v) => !v)}>{mobileList ? t('month.sheet') : t('month.dayList')}</button>
          </div>
        </div>
        <div className="shift-scroll">
          {loading && <div className="shift-empty">{t('common.loading')}</div>}
          {err && <div className="shift-login-err" style={{ margin: 12 }}>{err}</div>}
          {!loading && !data?.published && !data?.canEdit && !err && (
            <div className="shift-empty">{t('month.empty')}</div>
          )}
          {!loading && data && (data.published || data.canEdit) && mobileList && view === 'mine' && (data.dayHeaders || []).map((w) => {
            const a = byDay[w.day];
            const act = data.activitiesByDay?.[w.day] || data.activitiesByDay?.[String(w.day)];
            const note = data.dayNotes?.[`${phone}|${w.day}`] || '';
            return (
              <div key={w.day} className={`avail-day ${w.weekend ? 'weekend' : ''}`} style={{ margin: '0 12px 8px' }}>
                <div className="avail-day-head">
                  <span className="avail-day-num">{w.day}</span>
                  <span className="avail-day-wd">週{w.wd}</span>
                  <strong style={{ marginLeft: 'auto' }}>{a?.label || '—'}</strong>
                </div>
                {note ? <div className="shift-tb-sub" style={{ marginTop: 4 }}>{note}</div> : null}
                {act ? <div className="shift-tb-sub" style={{ marginTop: 4 }}>活動：{act}</div> : null}
              </div>
            );
          })}
          {!loading && data && (data.published || data.canEdit) && showSheet && (
            <div style={{ padding: 8 }}>
              <ScheduleSheet
                month={month}
                dayHeaders={data.dayHeaders || []}
                people={data.people || []}
                assignments={data.assignments || []}
                activitiesByDay={data.activitiesByDay || {}}
                shopNotesByDay={data.shopNotesByDay || {}}
                volunteersByDay={data.volunteersByDay || {}}
                dayNotes={data.dayNotes || {}}
                availabilityByPerson={data.availabilityByPerson || {}}
                canEdit={!!data.canEdit}
                canEditAvailability={!!data.canEdit}
                showAvailability={!!data.canEdit}
                highlightPhone={phone}
                minePhone={phone}
                view={view}
                saveStatus={saveStatus}
                onPatch={data.canEdit ? onPatch : undefined}
              />
            </div>
          )}
        </div>
    </>
  );
}
